import React, { useState } from "react";

const initial = { name: "", email: "", phone: "", projectType: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Required";
    if (!form.email.trim()) err.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Invalid email";
    if (!form.phone.trim()) err.phone = "Required";
    if (!form.projectType) err.projectType = "Required";
    if (!form.message.trim()) err.message = "Required";
    return err;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    setTouched({ name: true, email: true, phone: true, projectType: true, message: true });
    if (Object.keys(err).length === 0) {
      setSubmitted(true);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-28 md:py-40"
      style={{ backgroundColor: "#1a2330", color: "#f4f1eb" }}
    >
      <div className="container-editorial max-w-3xl">
        <div className="text-center md:text-left reveal mb-16 md:mb-24">
          <p
            className="text-[11px] uppercase font-body mb-6"
            style={{ color: "#c8b89a", letterSpacing: "0.36em" }}
          >
            — Let’s Work Together
          </p>
          <h2
            className="font-display leading-[1.02] tracking-[-0.015em]"
            style={{ fontSize: "clamp(2.25rem, 5.2vw, 5.25rem)", fontWeight: 400 }}
          >
            Ready to build something <em className="italic" style={{ color: "#c8b89a" }}>lasting?</em>
          </h2>
        </div>

        {submitted ? (
          <div
            data-testid="contact-success"
            className="reveal is-visible text-center md:text-left py-14 border-y"
            style={{ borderColor: "rgba(244,241,235,0.25)" }}
          >
            <p
              className="font-display italic leading-[1.15]"
              style={{ color: "#f4f1eb", fontSize: "clamp(1.75rem, 3.4vw, 3.25rem)" }}
            >
              Thank you — we’ll be in touch within 2 business days.
            </p>
            <p
              className="mt-5 font-body text-sm"
              style={{ color: "rgba(244,241,235,0.65)", letterSpacing: "0.1em" }}
            >
              — studio Brehaut architecture
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="contact-form"
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10"
          >
            <Field id="name" label="Name" value={form.name} onChange={set("name")} onBlur={blur("name")} error={errors.name} testid="contact-field-name" />
            <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} onBlur={blur("email")} error={errors.email} testid="contact-field-email" />
            <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} onBlur={blur("phone")} error={errors.phone} testid="contact-field-phone" />

            <SelectField
              id="projectType"
              label="Project Type"
              value={form.projectType}
              onChange={set("projectType")}
              error={errors.projectType}
              testid="contact-field-project-type"
              options={["Residential", "Commercial", "Institutional"]}
            />

            <div className="md:col-span-2">
              <Field
                id="message"
                label="Message"
                textarea
                value={form.message}
                onChange={set("message")}
                onBlur={blur("message")}
                error={errors.message}
                testid="contact-field-message"
              />
            </div>

            <div className="md:col-span-2 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6 mt-6">
              <p
                className="font-body text-[11px] uppercase"
                style={{ color: "rgba(244,241,235,0.55)", letterSpacing: "0.24em" }}
              >
                We respond within two business days.
              </p>
              <button
                type="submit"
                data-testid="contact-submit"
                className="group inline-flex items-center justify-center gap-3 h-13 px-9 py-4 rounded-full font-body text-[12px] uppercase transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(0,0,0,0.35)]"
                style={{
                  backgroundColor: "#3a7d7b",
                  color: "#f4f1eb",
                  letterSpacing: "0.24em",
                }}
              >
                Send Message
                <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
              </button>
            </div>
          </form>
        )}

        <div
          className="mt-20 md:mt-28 pt-10 border-t grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left font-body text-sm"
          style={{ borderColor: "rgba(244,241,235,0.18)", color: "rgba(244,241,235,0.78)" }}
        >
          <div>
            <p
              className="text-[10px] uppercase mb-3"
              style={{ color: "#c8b89a", letterSpacing: "0.32em" }}
            >
              Studio
            </p>
            <span data-testid="contact-location">Guernsey Cove, PEI</span>
            <p className="mt-1 text-[13px]" style={{ color: "rgba(244,241,235,0.55)" }}>
              Overlooking the Northumberland Strait
            </p>
          </div>
          <div>
            <p
              className="text-[10px] uppercase mb-3"
              style={{ color: "#c8b89a", letterSpacing: "0.32em" }}
            >
              Instagram
            </p>
            <a
              href="https://instagram.com/studiobrehaut"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              data-testid="contact-instagram"
            >
              @studiobrehaut
            </a>
          </div>
          <div>
            <p
              className="text-[10px] uppercase mb-3"
              style={{ color: "#c8b89a", letterSpacing: "0.32em" }}
            >
              Web
            </p>
            <a
              href="https://www.peiarchitect.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              data-testid="contact-web"
            >
              peiarchitect.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --- Field primitives --- */

const baseFieldStyle = {
  backgroundColor: "transparent",
  width: "100%",
  color: "#f4f1eb",
  border: 0,
  borderBottom: "1px solid rgba(244,241,235,0.28)",
  borderRadius: 0,
  outline: "none",
  padding: "10px 0 10px 0",
  fontSize: "16px",
  lineHeight: 1.5,
  fontFamily: "inherit",
  transition: "border-color 240ms ease",
};

const labelStyle = {
  display: "block",
  fontSize: "10px",
  letterSpacing: "0.28em",
  textTransform: "uppercase",
  color: "rgba(244,241,235,0.55)",
  marginBottom: "10px",
};

const errorStyle = { color: "#e6b7a3", marginTop: 8, fontSize: 12, letterSpacing: "0.04em" };

const Field = ({ id, label, type = "text", value, onChange, onBlur, error, testid, textarea }) => (
  <div className="min-w-0">
    <label htmlFor={id} style={labelStyle}>{label}</label>
    {textarea ? (
      <textarea
        id={id}
        rows={4}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        data-testid={testid}
        style={{
          ...baseFieldStyle,
          borderBottomColor: error ? "#c97f6a" : baseFieldStyle.borderBottom.split(" ").slice(-1)[0],
          resize: "vertical",
          minHeight: 110,
        }}
        onFocus={(e) => (e.currentTarget.style.borderBottomColor = "#3a7d7b")}
      />
    ) : (
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        data-testid={testid}
        style={{
          ...baseFieldStyle,
          borderBottomColor: error ? "#c97f6a" : "rgba(244,241,235,0.28)",
        }}
        onFocus={(e) => (e.currentTarget.style.borderBottomColor = "#3a7d7b")}
      />
    )}
    {error && (
      <p style={errorStyle} data-testid={`${testid}-error`}>
        {error}
      </p>
    )}
  </div>
);

const SelectField = ({ id, label, value, onChange, error, testid, options }) => (
  <div className="min-w-0">
    <label htmlFor={id} style={labelStyle}>{label}</label>
    <select
      id={id}
      value={value}
      onChange={onChange}
      data-testid={testid}
      style={{
        ...baseFieldStyle,
        borderBottomColor: error ? "#c97f6a" : "rgba(244,241,235,0.28)",
        appearance: "none",
        WebkitAppearance: "none",
        background:
          "transparent url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%23c8b89a' stroke-width='1.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\") no-repeat right 4px center",
        backgroundSize: "12px 8px",
        paddingRight: "24px",
      }}
      onFocus={(e) => (e.currentTarget.style.borderBottomColor = "#3a7d7b")}
    >
      <option value="" disabled style={{ color: "#1a2330" }}>
        Select one
      </option>
      {options.map((o) => (
        <option key={o} value={o} style={{ color: "#1a2330" }}>
          {o}
        </option>
      ))}
    </select>
    {error && (
      <p style={errorStyle} data-testid={`${testid}-error`}>
        {error}
      </p>
    )}
  </div>
);

export default Contact;
